/**
 * 音の素材を手続きで作る。音源ファイルは 1 つも持たない。
 *
 * 画像と同じ方針。単一 HTML ファイルで配ることが Web ファースト戦略の前提なので、
 * 数 MB の音声アセットを抱えると「URL を踏んで数秒で泳ぎ出す」が成立しなくなる。
 * 波の音も効果音も BGM も、すべて起動時に計算で作る。
 */

/** ホワイトノイズ。しぶきと波の芯になる。 */
export function whiteNoise(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/**
 * ピンクノイズ（低い成分が強いノイズ）。
 * ホワイトノイズのままだと「砂嵐」に聞こえる。海鳴りには低い成分が要る。
 * Paul Kellet の近似フィルタを使う。
 */
export function pinkNoise(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
    b6 = white * 0.115926;
    data[i] = pink * 0.11;
  }

  // 継ぎ目が聞こえないよう、ループ端をクロスフェードする。
  const fade = Math.min(Math.floor(ctx.sampleRate * 0.25), Math.floor(length / 4));
  for (let i = 0; i < fade; i++) {
    const t = i / fade;
    data[i] = data[i] * t + data[length - fade + i] * (1 - t);
  }
  return buffer;
}

/**
 * 撥弦音（Karplus-Strong）。ウクレレの代わりに使う。
 * ノイズをリングバッファに入れ、隣り合う標本を平均しながら回すと弦になる。
 *
 * @param decay 0〜1。1 に近いほど長く鳴る。
 * @param damping 0〜1。大きいほど丸い音（高い成分が早く減る）。
 */
export function pluck(
  ctx: BaseAudioContext,
  frequency: number,
  seconds: number,
  decay = 0.996,
  damping = 0.5,
): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);

  const period = Math.max(2, Math.round(sampleRate / frequency));
  const ring = new Float32Array(period);
  for (let i = 0; i < period; i++) ring[i] = Math.random() * 2 - 1;

  let index = 0;
  for (let i = 0; i < length; i++) {
    const current = ring[index];
    const next = ring[(index + 1) % period];
    ring[index] = (current * (1 - damping) + next * damping) * decay;
    // 終端を絞って、ぶつ切りの気配を消す。
    const tail = Math.min(1, (length - i) / (sampleRate * 0.05));
    data[i] = current * tail;
    index = (index + 1) % period;
  }
  return buffer;
}

/** 半音番号から周波数へ。A4 = 440 Hz を基準にする。 */
export function noteToFreq(semitonesFromA4: number): number {
  return 440 * Math.pow(2, semitonesFromA4 / 12);
}
